import React, { useEffect, useRef, useState, useCallback } from 'react';
import { UploadStaffSelfie as BLUploadStaffSelfie } from './../../../utilities/BL/SelfieFan';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';

// -----------------------------------------------------------------------------
// StaffCam.jsx
// Herramienta exclusiva para el Staff.
// Permite tomar fotos ágilmente o subirlas desde la galería sin restricciones de tiempo.
// -----------------------------------------------------------------------------

const SelfieFan = () => {
  const { showMessage } = useFloatMessage();

  // --- REFERENCIAS AL DOM ---
  // videoElementRef: Conecta el flujo de la cámara web con la etiqueta <video> HTML
  const videoElementRef = useRef(null);
  // canvasElementRef: Usado internamente para dibujar el cuadro de video y convertirlo a Blob (imagen)
  const canvasElementRef = useRef(null);
  // fileInputReference: Usado para abrir el selector de archivos del celular (galería) programáticamente
  const fileInputReference = useRef(null);

  // --- REFERENCIAS DE MEMORIA (No causan re-render) ---
  const mediaStreamReference = useRef(null);
  const baseImageBitmapReference = useRef(null); // Imagen base cruda para poder rotarla sin perder calidad
  const previewObjectUrlReference = useRef(null); // URL temporal en memoria del navegador para mostrar la vista previa

  // --- ESTADOS DE LA INTERFAZ ---
  // Vistas disponibles: 'camera_view' | 'preview_view' | 'success_view'
  const [currentInterfaceView, setCurrentInterfaceView] = useState("camera_view");
  
  // --- ESTADOS DE PROCESO ---
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const [isCameraReadyForCapture, setIsCameraReadyForCapture] = useState(false);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [isUploadingToServer, setIsUploadingToServer] = useState(false);

  // --- ESTADOS DE DATOS DE IMAGEN ---
  const [activePreviewImageUrl, setActivePreviewImageUrl] = useState(null);
  const [finalImageBlobToUpload, setFinalImageBlobToUpload] = useState(null);
  const [imageRotationDegrees, setImageRotationDegrees] = useState(0); // Controla los giros: 0, 90, 180, 270
  
  // Dimensiones máximas recomendadas para exportar la imagen
  const MAXIMUM_EXPORT_WIDTH = 1280;
  const MAXIMUM_EXPORT_HEIGHT = 1280;

  // =========================================================================
  // CONTROL DE LA CÁMARA DEL DISPOSITIVO
  // =========================================================================

  const stopHardwareCamera = useCallback(() => {
    const currentStream = mediaStreamReference.current;
    if (currentStream) {
      // Detiene todos los tracks (video/audio) para liberar la cámara del celular
      currentStream.getTracks().forEach((mediaTrack) => mediaTrack.stop());
      mediaStreamReference.current = null;
    }
  }, []);

//   const initializeHardwareCamera = useCallback(async () => {
//     setIsCameraStarting(true);
//     setIsCameraReadyForCapture(false);
//     setCurrentInterfaceView("camera_view");

//     try {
//       // Solicitamos la cámara trasera (environment) idealmente para el staff, o dejamos que el OS decida
//       const hardwareStream = await navigator.mediaDevices.getUserMedia({
//         video: { facingMode: { ideal: "environment" } }, 
//         audio: false,
//       });
//       mediaStreamReference.current = hardwareStream;
//     } catch (cameraAccessError) {
//       console.error("Error obteniendo cámara:", cameraAccessError);
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text: "Error al acceder a la cámara. Revisa los permisos del navegador.",
//       });
//     } finally {
//       setIsCameraStarting(false);
//     }
//   }, [showMessage]);

//   // Efecto que conecta el flujo de video al elemento HTML cuando la vista es 'camera_view'
//   useEffect(() => {
//     if (currentInterfaceView !== "camera_view") return;

//     const videoNode = videoElementRef.current;
//     const activeStream = mediaStreamReference.current;

//     if (!videoNode || !activeStream) return;

//     videoNode.srcObject = activeStream;
//     videoNode.muted = true;
//     videoNode.setAttribute("playsinline", "true"); // Requerido para iOS Safari

//     // Intentamos reproducir el video de inmediato
//     videoNode.play()
//       .then(() => setIsCameraReadyForCapture(true))
//       .catch((error) => console.error("Error auto-play video:", error));

//   }, [currentInterfaceView]);

const initializeHardwareCamera = useCallback(async () => {
    setIsCameraStarting(true);
    setIsCameraReadyForCapture(false);
    setCurrentInterfaceView("camera_view");

    try {
      // Solicitamos la cámara trasera (environment) idealmente para el staff
      const hardwareStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } }, 
        audio: false,
      });
      
      // Guardamos la referencia para no perderla
      mediaStreamReference.current = hardwareStream;

      // SOLUCIÓN 1: Conectamos el video DIRECTAMENTE tan pronto lo obtenemos
      const videoNode = videoElementRef.current;
      if (videoNode) {
        videoNode.srcObject = hardwareStream;
        videoNode.muted = true;
        videoNode.setAttribute("playsinline", "true"); // Crucial para Safari/iOS

        // SOLUCIÓN 2: Habilitar el botón solo cuando el video reporta que tiene datos (pixeles)
        videoNode.onloadeddata = () => {
           setIsCameraReadyForCapture(true);
        };

        // Forzamos la reproducción
        videoNode.play().catch((error) => console.error("Error auto-play video:", error));
      }

    } catch (cameraAccessError) {
      console.error("Error obteniendo cámara:", cameraAccessError);
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: "Error al acceder a la cámara. Revisa y autoriza los permisos del navegador.",
      });
    } finally {
      setIsCameraStarting(false);
    }
  }, [showMessage]);

  // Efecto de respaldo: Reconecta el flujo de video si el usuario navega entre vistas
  // y regresa a la cámara (por ejemplo, si cancela una foto y vuelve a empezar)
  useEffect(() => {
    if (currentInterfaceView !== "camera_view" || isCameraStarting) return;

    const videoNode = videoElementRef.current;
    const activeStream = mediaStreamReference.current;

    // Si ya tenemos el stream y el nodo del video, pero no están conectados
    if (videoNode && activeStream && videoNode.srcObject !== activeStream) {
      videoNode.srcObject = activeStream;
      videoNode.muted = true;
      videoNode.setAttribute("playsinline", "true");

      videoNode.onloadeddata = () => {
         setIsCameraReadyForCapture(true);
      };

      videoNode.play().catch((error) => console.error("Error auto-play video (Fallback):", error));
    }
    
    // Limpieza del evento para evitar fugas de memoria
    return () => {
       if (videoNode) videoNode.onloadeddata = null;
    };
  }, [currentInterfaceView, isCameraStarting]);

  // Limpieza general al desmontar el componente (cuando el usuario cierra la página)
  useEffect(() => {
    return () => {
      stopHardwareCamera();
      if (previewObjectUrlReference.current) {
        URL.revokeObjectURL(previewObjectUrlReference.current);
      }
    };
  }, [stopHardwareCamera]);

  // =========================================================================
  // PROCESAMIENTO Y ROTACIÓN DE IMÁGENES
  // =========================================================================

  // Convierte un objeto Blob en un ImageBitmap nativo para poder dibujarlo rápido en un Canvas
  const buildBaseBitmapFromBlob = async (sourceBlob) => {
    if (!sourceBlob) return;
    
    // Fallback para navegadores antiguos (Safari viejo) si no existe createImageBitmap
    if (!("createImageBitmap" in window)) {
      const temporaryObjectUrl = URL.createObjectURL(sourceBlob);
      return new Promise((resolve, reject) => {
        const imageElement = new Image();
        imageElement.onload = () => {
          baseImageBitmapReference.current = imageElement;
          URL.revokeObjectURL(temporaryObjectUrl);
          resolve();
        };
        imageElement.onerror = (error) => reject(error);
        imageElement.src = temporaryObjectUrl;
      });
    }

    baseImageBitmapReference.current = await createImageBitmap(sourceBlob);
  };

  // Dibuja el Bitmap base en el Canvas aplicando la rotación actual, y genera el Blob final
  // const renderCanvasAndGenerateBlob = async (rotationAngle) => {
  //   const sourceBitmap = baseImageBitmapReference.current;
  //   const canvasNode = canvasElementRef.current;
    
  //   if (!sourceBitmap || !canvasNode) return;

  //   const context2D = canvasNode.getContext("2d");
  //   const sourceWidth = sourceBitmap.width;
  //   const sourceHeight = sourceBitmap.height;

  //   // Si la rotación es de 90 o 270 grados, invertimos ancho y alto del canvas de salida
  //   const isQuarterTurnRotation = rotationAngle % 180 !== 0;
  //   const finalCanvasWidth = isQuarterTurnRotation ? sourceHeight : sourceWidth;
  //   const finalCanvasHeight = isQuarterTurnRotation ? sourceWidth : sourceHeight;

  //   canvasNode.width = finalCanvasWidth;
  //   canvasNode.height = finalCanvasHeight;

  //   context2D.save();
    
  //   // Lógica matemática para rotar el contexto desde su centro
  //   switch (rotationAngle % 360) {
  //     case 90:
  //       context2D.translate(finalCanvasWidth, 0);
  //       context2D.rotate(Math.PI / 2);
  //       break;
  //     case 180:
  //       context2D.translate(finalCanvasWidth, finalCanvasHeight);
  //       context2D.rotate(Math.PI);
  //       break;
  //     case 270:
  //       context2D.translate(0, finalCanvasHeight);
  //       context2D.rotate((3 * Math.PI) / 2);
  //       break;
  //     default:
  //       // 0 grados, no requiere traslación ni rotación
  //       break;
  //   }

  //   // Dibujamos la imagen original y restauramos el contexto
  //   context2D.drawImage(sourceBitmap, 0, 0);
  //   context2D.restore();

  //   // Exportamos el resultado a un Blob optimizado (JPEG al 90% de calidad)
  //   const processedBlob = await new Promise((resolve, reject) => {
  //     canvasNode.toBlob(
  //       (resultingBlob) => resultingBlob ? resolve(resultingBlob) : reject(new Error("Fallo exportación a Blob")),
  //       "image/jpeg",
  //       0.90
  //     );
  //   });

  //   // Actualizamos la URL temporal para que el componente React la muestre en pantalla
  //   const newPreviewObjectUrl = URL.createObjectURL(processedBlob);
  //   if (previewObjectUrlReference.current) {
  //     URL.revokeObjectURL(previewObjectUrlReference.current); // Evitar fugas de memoria
  //   }
    
  //   previewObjectUrlReference.current = newPreviewObjectUrl;
  //   setActivePreviewImageUrl(newPreviewObjectUrl);
  //   setFinalImageBlobToUpload(processedBlob);
  // };

  // Dibuja el Bitmap base en el Canvas aplicando rotación, redimensionado automático y compresión
  const renderCanvasAndGenerateBlob = async (rotationAngle) => {
    const sourceBitmap = baseImageBitmapReference.current;
    const canvasNode = canvasElementRef.current;
    
    if (!sourceBitmap || !canvasNode) return;

    const context2D = canvasNode.getContext("2d");
    
    // 1. Dimensiones originales de la imagen pura
    const sourceWidth = sourceBitmap.width;
    const sourceHeight = sourceBitmap.height;

    // 2. Dimensiones de la imagen una vez rotada (límites)
    const isQuarterTurnRotation = rotationAngle % 180 !== 0;
    const boundedWidth = isQuarterTurnRotation ? sourceHeight : sourceWidth;
    const boundedHeight = isQuarterTurnRotation ? sourceWidth : sourceHeight;

    // 3. CÁLCULO DE REDIMENSIONAMIENTO (LA MAGIA DE LA COMPRESIÓN)
    // Fijamos un límite máximo. 1280px es calidad HD, perfecta para pantallas gigantes sin pesar mucho.
    const MAXIMUM_EXPORT_SIZE = 1280; 
    let scaleFactor = 1;

    if (boundedWidth > MAXIMUM_EXPORT_SIZE || boundedHeight > MAXIMUM_EXPORT_SIZE) {
      // Obtenemos la proporción para no deformar la imagen
      scaleFactor = Math.min(MAXIMUM_EXPORT_SIZE / boundedWidth, MAXIMUM_EXPORT_SIZE / boundedHeight);
    }

    // 4. Asignamos el tamaño físico final al Canvas ya escalado
    const finalCanvasWidth = Math.round(boundedWidth * scaleFactor);
    const finalCanvasHeight = Math.round(boundedHeight * scaleFactor);

    canvasNode.width = finalCanvasWidth;
    canvasNode.height = finalCanvasHeight;

    context2D.save();
    
    // Aplicamos la escala al contexto para que todo lo que se dibuje se reduzca
    context2D.scale(scaleFactor, scaleFactor);
    
    // 5. Lógica matemática para rotar el contexto desde su centro (basado en dimensiones sin escalar)
    switch (rotationAngle % 360) {
      case 90:
        context2D.translate(boundedWidth, 0);
        context2D.rotate(Math.PI / 2);
        break;
      case 180:
        context2D.translate(boundedWidth, boundedHeight);
        context2D.rotate(Math.PI);
        break;
      case 270:
        context2D.translate(0, boundedHeight);
        context2D.rotate((3 * Math.PI) / 2);
        break;
      default:
        // 0 grados, no requiere traslación
        break;
    }

    // Dibujamos la imagen original y restauramos el contexto
    context2D.drawImage(sourceBitmap, 0, 0);
    context2D.restore();

    // 6. EXPORTACIÓN ÓPTIMA: Generamos un JPEG al 85% de calidad.
    // (Bajar de 0.92 a 0.85 reduce el peso casi a la mitad y el ojo humano no nota la diferencia)
    const processedBlob = await new Promise((resolve, reject) => {
      canvasNode.toBlob(
        (resultingBlob) => resultingBlob ? resolve(resultingBlob) : reject(new Error("Fallo exportación a Blob")),
        "image/jpeg",
        0.85 
      );
    });

    // Actualizamos la URL temporal para que el componente React la muestre en pantalla
    const newPreviewObjectUrl = URL.createObjectURL(processedBlob);
    if (previewObjectUrlReference.current) {
      URL.revokeObjectURL(previewObjectUrlReference.current); // Evitar fugas de memoria
    }
    
    previewObjectUrlReference.current = newPreviewObjectUrl;
    setActivePreviewImageUrl(newPreviewObjectUrl);
    setFinalImageBlobToUpload(processedBlob);
  };

  // =========================================================================
  // ACCIONES DEL USUARIO (CAPTURAR, SELECCIONAR GALERÍA, ROTAR, ENVIAR)
  // =========================================================================

  // Acción 1: Capturar cuadro del <video> actual
  // const handleCaptureFromVideo = async () => {
  //   const videoNode = videoElementRef.current;
  //   const canvasNode = canvasElementRef.current;
  //   if (!videoNode || !canvasNode) return;

  //   setIsProcessingImage(true);

  //   try {
  //     const context2D = canvasNode.getContext("2d");
  //     const videoRawWidth = videoNode.videoWidth;
  //     const videoRawHeight = videoNode.videoHeight;

  //     // Calcular dimensiones para no exceder los límites configurados
  //     let calculatedWidth = videoRawWidth;
  //     let calculatedHeight = videoRawHeight;
  //     const aspectRatio = videoRawWidth / videoRawHeight;

  //     if (calculatedWidth > MAXIMUM_EXPORT_WIDTH) {
  //       calculatedWidth = MAXIMUM_EXPORT_WIDTH;
  //       calculatedHeight = Math.round(MAXIMUM_EXPORT_WIDTH / aspectRatio);
  //     }
  //     if (calculatedHeight > MAXIMUM_EXPORT_HEIGHT) {
  //       calculatedHeight = MAXIMUM_EXPORT_HEIGHT;
  //       calculatedWidth = Math.round(MAXIMUM_EXPORT_HEIGHT * aspectRatio);
  //     }

  //     canvasNode.width = calculatedWidth;
  //     canvasNode.height = calculatedHeight;

  //     // Nota: Si usas la cámara trasera (environment), NO debes espejearla. 
  //     // Si usas la frontal (user), descomenta las líneas de translate y scale.
  //     // context2D.translate(calculatedWidth, 0);
  //     // context2D.scale(-1, 1);
      
  //     context2D.drawImage(videoNode, 0, 0, calculatedWidth, calculatedHeight);

  //     // Extraer a Blob inicial
  //     const capturedBlob = await new Promise((resolve) => {
  //       canvasNode.toBlob((blobResult) => resolve(blobResult), "image/jpeg", 0.90);
  //     });

  //     // Preparar el motor de previsualización
  //     await buildBaseBitmapFromBlob(capturedBlob);
  //     setImageRotationDegrees(0);
  //     await renderCanvasAndGenerateBlob(0);

  //     // Apagamos la cámara web física y avanzamos a la vista de revisión
  //     stopHardwareCamera();
  //     setCurrentInterfaceView("preview_view");

  //   } catch (captureError) {
  //     console.error(captureError);
  //     showMessage({ type: MESSAGE_TYPE.BAD, text: "Hubo un error al procesar la captura." });
  //   } finally {
  //     setIsProcessingImage(false);
  //   }
  // };

  // Acción 1: Capturar cuadro del <video> actual
  const handleCaptureFromVideo = async () => {
    const videoNode = videoElementRef.current;
    const canvasNode = canvasElementRef.current;
    if (!videoNode || !canvasNode) return;

    setIsProcessingImage(true);

    try {
      const context2D = canvasNode.getContext("2d");
      
      // Simplemente extraemos la imagen cruda tal cual viene del video
      canvasNode.width = videoNode.videoWidth;
      canvasNode.height = videoNode.videoHeight;
      context2D.drawImage(videoNode, 0, 0, videoNode.videoWidth, videoNode.videoHeight);

      // Generamos un blob temporal de alta calidad
      const capturedBlob = await new Promise((resolve) => {
        canvasNode.toBlob((blobResult) => resolve(blobResult), "image/jpeg", 1.0);
      });

      // El motor de previsualización (renderCanvasAndGenerateBlob) se encargará 
      // de redimensionarlo y comprimirlo al 85% en el siguiente paso.
      await buildBaseBitmapFromBlob(capturedBlob);
      setImageRotationDegrees(0);
      await renderCanvasAndGenerateBlob(0);

      stopHardwareCamera();
      setCurrentInterfaceView("preview_view");

    } catch (captureError) {
      console.error(captureError);
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Hubo un error al procesar la captura." });
    } finally {
      setIsProcessingImage(false);
    }
  };

  // Acción 2: Manejar archivo subido desde el Input HTML (Galería)
  const handleFileSelectionFromGallery = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    setIsProcessingImage(true);
    
    try {
      // Iniciar cámara web si estaba prendida la apagamos porque usaremos un archivo
      stopHardwareCamera();

      // Convertimos el archivo local directamente en nuestro Bitmap de trabajo
      await buildBaseBitmapFromBlob(selectedFile);
      setImageRotationDegrees(0);
      await renderCanvasAndGenerateBlob(0);

      // Limpiamos el input para que permita seleccionar el mismo archivo de nuevo si se arrepiente
      event.target.value = null; 
      
      setCurrentInterfaceView("preview_view");

    } catch (fileError) {
      console.error(fileError);
      showMessage({ type: MESSAGE_TYPE.BAD, text: "El archivo seleccionado no es válido o está corrupto." });
    } finally {
      setIsProcessingImage(false);
    }
  };

  // Acción 3: Rotar imagen actual
  const handleRotateImageCommand = async () => {
    const nextRotationDegrees = (imageRotationDegrees + 90) % 360;
    setImageRotationDegrees(nextRotationDegrees);
    await renderCanvasAndGenerateBlob(nextRotationDegrees);
  };

  // Acción 4: Enviar la imagen definitiva al Backend
  const handleUploadDataToServer = async () => {
    if (!finalImageBlobToUpload) return;

    setIsUploadingToServer(true);

    try {
      // Preparamos el Payload. Para el Staff, mandamos un caption vacío por ahora.
      const payloadInformation = {
        blob: finalImageBlobToUpload,
        caption: "Foto Staff" // Opcional, puedes cambiarlo o dejarlo vacío
      };

      const serverResponse = await BLUploadStaffSelfie(payloadInformation);

      if (serverResponse.success) {
        // Todo exitoso, avanzamos a la pantalla verde
        setCurrentInterfaceView("success_view");
      } else {
        showMessage({ type: MESSAGE_TYPE.BAD, text: serverResponse.message });
      }

    } catch (uploadError) {
      console.error(uploadError);
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Error crítico al contactar al servidor." });
    } finally {
      setIsUploadingToServer(false);
    }
  };

  // Acción 5: Cancelar foto actual y volver a empezar
  const handleRestartProcess = async () => {
    setActivePreviewImageUrl(null);
    setFinalImageBlobToUpload(null);
    setImageRotationDegrees(0);
    
    if (previewObjectUrlReference.current) {
      URL.revokeObjectURL(previewObjectUrlReference.current);
      previewObjectUrlReference.current = null;
    }

    await initializeHardwareCamera();
  };

  // =========================================================================
  // RENDERIZADO DE LA INTERFAZ
  // =========================================================================

  // Iniciar la cámara apenas el componente se monta por primera vez
  useEffect(() => {
    initializeHardwareCamera();
  }, [initializeHardwareCamera]);

  return (
    <div className="container SelfieFan StaffCam">
      {/* Canvas oculto usado como motor de renderizado gráfico */}
      <canvas ref={canvasElementRef} className="d-none" />

      {/* Input oculto para acceder al sistema de archivos / galería nativa */}
      <input 
        type="file" 
        accept="image/*" 
        ref={fileInputReference} 
        onChange={handleFileSelectionFromGallery}
        className="d-none" 
      />

      {/* VISTA 1: CÁMARA ACTIVA */}
      {currentInterfaceView === "camera_view" && (
        <div className="SelfieFan__Overlay bg-dark d-flex flex-column justify-content-center align-items-center vh-100">
          <div className="SelfieFan__Overlay__VideoWrapper w-100 position-relative">
            <video
              ref={videoElementRef}
              className="SelfieFan__Overlay__Video w-100 h-100 object-fit-cover"
              autoPlay
              playsInline
              muted
            />
            
            {/* Controles Flotantes sobre el Video */}
            <div className="position-absolute bottom-0 start-50 translate-middle-x mb-4 d-flex gap-3">
              
              {/* Botón: Abrir Galería */}
              <button
                className="btn btn-secondary rounded-circle p-3 shadow"
                onClick={() => fileInputReference.current?.click()}
                disabled={isProcessingImage}
                title="Subir foto desde Galería"
              >
                <i className="bi bi-images fs-3" />
              </button>

              {/* Botón: Disparador Cámara */}
              <button
                className="btn btn-danger rounded-circle p-4 shadow"
                onClick={handleCaptureFromVideo}
                disabled={!isCameraReadyForCapture || isProcessingImage}
                title="Tomar Foto"
              >
                {isProcessingImage ? (
                  <i className="bi bi-hourglass-split fs-2" />
                ) : (
                  <i className="bi bi-camera fs-2" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VISTA 2: REVISIÓN DE IMAGEN CAPTURADA */}
      {currentInterfaceView === "preview_view" && (
        <div className="SelfieFan__Preview container py-4 d-flex flex-column align-items-center">
          <h3 className="mb-3 text-center">Revisar Fotografía (STAFF)</h3>
          
          {/* Controles superiores de edición rápida */}
          <div className="SelfieFan__Preview__RotateActions d-flex justify-content-center gap-2 mb-3">
            <button
              type="button"
              className="btn btn-outline-dark"
              disabled={!baseImageBitmapReference.current || isProcessingImage}
              onClick={handleRotateImageCommand}
            >
              <i className="bi bi-arrow-clockwise" /> Girar 90°
            </button>
            
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={handleRestartProcess}
              disabled={isUploadingToServer}
            >
              <i className="bi bi-x-circle" /> Descartar
            </button>
          </div>

          {/* Muestra de la imagen generada en memoria */}
          {activePreviewImageUrl && (
            <img
              src={activePreviewImageUrl}
              alt="Vista previa a subir"
              className="SelfieFan__Preview__Image img-fluid rounded shadow mb-4"
              style={{ maxHeight: '60vh', objectFit: 'contain' }}
            />
          )}

          {/* Botón principal de acción al servidor */}
          <button
            className="btn btn-danger btn-lg w-100"
            onClick={handleUploadDataToServer}
            disabled={!finalImageBlobToUpload || isUploadingToServer}
          >
            {isUploadingToServer ? (
              <><span className="spinner-border spinner-border-sm me-2" /> Enviando...</>
            ) : (
              <><i className="bi bi-cloud-arrow-up-fill me-2" /> Subir a Pantalla</>
            )}
          </button>
        </div>
      )}

      {/* VISTA 3: ÉXITO INMEDIATO (SIN COOLDOWN) */}
      {currentInterfaceView === "success_view" && (
        <div className="SelfieFan__Sent container py-5 text-center d-flex flex-column align-items-center justify-content-center min-vh-100">
          <p className="display-1 text-success mb-3">
            <i className="bi bi-check-circle-fill" />
          </p>
          <h2 className="mb-4">¡Subida Exitosa!</h2>
          <p className="text-muted mb-5">La foto ya se encuentra disponible en el panel de control del estadio.</p>
          
          {/* Al ser Staff, ofrecemos seguir tomando fotos inmediatamente */}
          <button
            className="btn btn-danger btn-lg px-5 shadow"
            onClick={handleRestartProcess}
          >
            <i className="bi bi-camera me-2" /> Tomar Siguiente Foto
          </button>
        </div>
      )}
    </div>
  );
};

export { SelfieFan };